import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Tag from "../../components/ui/Tag/Tag.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META, PRICE_FROM } from "../../seo/siteMeta.js";
import { breadcrumbSchema } from "../../seo/schema.js";
import { sortedPosts, CATEGORIES } from "../../data/blogPosts.js";
import { blogImage } from "../../data/blogImages.js";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";

const meta = ROUTE_META["/blog"];

const BlogIndex = () => {
  const posts = sortedPosts();

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/blog"
        keywords={meta.keywords}
        alternates={[{ hreflang: "es", path: "/es" }]}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ]}
      />

      <div className="flex justify-end">
        <Link to="/es" hrefLang="es" className="text-sm font-semibold text-[var(--accent)] no-underline">
          Leer en español
        </Link>
      </div>
      <PageHeader eyebrow="Guides" title="Form 2290 & HVUT guides">
        Deadlines, amendments, VIN corrections, credits, and stamped Schedule 1 — written from the IRS
        Instructions for Form 2290 (Rev. July 2026), not from hearsay.
      </PageHeader>

      {/* Horizontal card grid rather than the single stacked column this used
          to be: eight full-width rows made the page a long scroll with no sense
          of how much was on offer. Three across shows the whole library at once.

          Each card leads with its own image and its own alt text — see
          src/data/blogImages.js. Cards whose image has not been generated yet
          simply render without one, because MarketingArt returns null on a
          404. */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {posts.map((post) => {
          const img = blogImage(post.slug);
          return (
            <Panel key={post.slug} pop className="overflow-hidden flex flex-col">
              <Link to={`/blog/${post.slug}`} className="no-underline text-inherit flex flex-col h-full">
                {img && (
                  <div className="aspect-[16/10] overflow-hidden bg-[var(--panel-2)]">
                    <MarketingArt
                      src={img.file}
                      alt={img.alt}
                      className="w-full h-full object-cover block"
                    />
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Tag tone="default">{CATEGORIES[post.category]}</Tag>
                    <span className="text-[var(--muted)] text-xs inline-flex items-center gap-1">
                      <Clock size={13} /> {post.readingMinutes} min read
                    </span>
                  </div>

                  <h2 className="font-disp font-bold text-[18px] leading-[1.3] tracking-[-.01em] mt-2.5">
                    {post.title}
                  </h2>
                  <p className="text-[var(--muted)] text-[14px] leading-[1.6] mt-2">{post.excerpt}</p>

                  {/* mt-auto pins this to the bottom so the link sits on the
                      same baseline across cards of differing text length. */}
                  <span className="text-[var(--accent)] text-sm font-semibold inline-flex items-center gap-1 mt-auto pt-3">
                    Read guide <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            </Panel>
          );
        })}
      </div>

      <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-11 !p-[30px]">
        <div>
          <b className="font-disp text-[19px]">Ready to file?</b>
          <p className="text-[var(--muted)] text-sm mt-1 max-w-[46ch]">
            E-file Form 2290 from ${PRICE_FROM} and get your stamped Schedule 1 in seconds.
          </p>
        </div>
        <Link to="/wizard/1">
          <Button>
            Start filing <ArrowRight size={16} />
          </Button>
        </Link>
      </Panel>
    </div>
  );
};

export default BlogIndex;
